import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { confirmPassword, forgotPassword } from '../utils/cognitoAuth';
import { useForm } from '../hooks/useForm';
import loginImage from '../assets/cc-login.png';
import './Login.css';

const passwordRequirements = [
  {
    label: 'At least 8 characters',
    test: (value) => value.length >= 8,
  },
  {
    label: 'One uppercase letter',
    test: (value) => /[A-Z]/.test(value),
  },
  {
    label: 'One lowercase letter',
    test: (value) => /[a-z]/.test(value),
  },
  {
    label: 'One number',
    test: (value) => /[0-9]/.test(value),
  },
  {
    label: 'One special character',
    test: (value) => /[^A-Za-z0-9]/.test(value),
  },
];

const getErrorMessage = (error) => {
  const errorMessages = {
    UserNotFoundException: 'If the email exists, a code has been sent.',
    CodeMismatchException: 'Invalid verification code. Please check your email and try again.',
    ExpiredCodeException: 'Verification code expired. Please request a new code.',
    InvalidPasswordException: 'Password does not meet requirements.',
    LimitExceededException: 'Too many attempts. Please try again later.',
    InvalidParameterException: 'Invalid input. Please check your information.',
  };

  if (!error) {
    return 'An unexpected error occurred. Please try again.';
  }

  return errorMessages[error.code] || error.message || 'An unexpected error occurred.';
};

const validatePassword = (password) =>
  passwordRequirements
    .filter((requirement) => !requirement.test(password))
    .map((requirement) => requirement.label);

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPasswordValue, setConfirmPasswordValue] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [stepTwoLoading, setStepTwoLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(3);

  const { values, errors, loading, handleChange, handleSubmit, setError, setValues } = useForm({
    email: '',
  });

  useEffect(() => {
    if (step !== 3) {
      return undefined;
    }

    setRedirectCountdown(3);
    const countdownInterval = setInterval(() => {
      setRedirectCountdown((prev) => Math.max(prev - 1, 0));
    }, 1000);

    const redirectTimer = setTimeout(() => {
      navigate('/login');
    }, 3000);

    return () => {
      clearInterval(countdownInterval);
      clearTimeout(redirectTimer);
    };
  }, [navigate, step]);

  const onSubmitStep1 = handleSubmit(async (formValues) => {
    setSubmitError('');
    setInfoMessage('');

    try {
      await forgotPassword(formValues.email);
      setEmail(formValues.email);
      setStep(2);
    } catch (err) {
      if (err?.code === 'UserNotFoundException') {
        setEmail(formValues.email);
        setStep(4);
        return;
      }

      setError('general', getErrorMessage(err));
    }
  });

  const onSubmitStep2 = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (!code || code.length < 6) {
      setSubmitError('Please enter the 6-digit verification code.');
      return;
    }

    if (newPassword !== confirmPasswordValue) {
      setSubmitError('Passwords do not match.');
      return;
    }

    if (validatePassword(newPassword).length > 0) {
      setSubmitError('Password does not meet requirements.');
      return;
    }

    try {
      setStepTwoLoading(true);
      await confirmPassword(email, code, newPassword);
      setStep(3);
    } catch (err) {
      setSubmitError(getErrorMessage(err));
    } finally {
      setStepTwoLoading(false);
    }
  };

  const handleBackToStep1 = () => {
    setStep(1);
    setCode('');
    setNewPassword('');
    setConfirmPasswordValue('');
    setSubmitError('');
    setInfoMessage('');
    setShowPassword(false);
    setShowConfirmPassword(false);
    setValues((prev) => ({
      ...prev,
      email,
    }));
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <img src={loginImage} alt="Policy Central" className="login-header-image" />
        </div>

        {step === 1 && (
          <form className="login-form" onSubmit={onSubmitStep1}>
            <div className="step-indicator">Step 1 of 2</div>
            <h3>Reset Password</h3>
            <p className="form-description">
              Enter your email address and we'll send a verification code to reset your
              password. The email may come from <strong>no-reply@verificationemail.com</strong>. 
              Please check your spam/junk folder.
            </p>

            {errors.general && (
              <div className="alert alert-danger" role="alert">
                {errors.general}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email">Email address</label>
              <input
                type="email"
                id="email"
                name="email"
                className="form-control"
                value={values.email}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-block forgot-password-submit"
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send Reset Code'}
            </button>

            <div className="signup-link">
              <Link to="/login">Back to Sign In</Link>
            </div>
          </form>
        )}

        {step === 2 && (
          <form className="login-form" onSubmit={onSubmitStep2}>
            <div className="step-indicator">Step 2 of 2</div>
            <h3>Enter Verification Code</h3>
            <p className="form-description">
              Enter the 6-digit code sent to <strong>{email}</strong> and choose a new
              password. The email may come from <strong>no-reply@verificationemail.com</strong>. 
              Please check your spam/junk folder.
            </p>

            {infoMessage && (
              <div className="alert alert-info" role="alert">
                {infoMessage}
              </div>
            )}

            {submitError && (
              <div className="alert alert-danger" role="alert">
                {submitError}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="code">Verification code</label>
              <input
                type="text"
                id="code"
                name="code"
                className="form-control code-input"
                value={code}
                onChange={(event) => {
                  const sanitized = event.target.value.replace(/\D/g, '').slice(0, 6);
                  setCode(sanitized);
                }}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="Enter code"
                required
                disabled={stepTwoLoading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">New password</label>
              <div className="password-field">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="newPassword"
                  name="newPassword"
                  className="form-control"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  required
                  disabled={stepTwoLoading}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  disabled={stepTwoLoading}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm password</label>
              <div className="password-field">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  className="form-control"
                  value={confirmPasswordValue}
                  onChange={(event) => setConfirmPasswordValue(event.target.value)}
                  required
                  disabled={stepTwoLoading}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  disabled={stepTwoLoading}
                >
                  {showConfirmPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <div className="password-requirements" aria-live="polite">
              <p>Password must include:</p>
              <ul>
                {passwordRequirements.map((requirement) => {
                  const met = requirement.test(newPassword);
                  return (
                    <li
                      key={requirement.label}
                      className={`password-requirement ${met ? 'met' : 'unmet'}`}
                    >
                      <span className="requirement-icon">{met ? '✓' : '•'}</span>
                      {requirement.label}
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="reset-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleBackToStep1}
                disabled={stepTwoLoading}
              >
                Back
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={stepTwoLoading}
              >
                {stepTwoLoading ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>

            <div className="signup-link">
              <Link to="/login">Back to Sign In</Link>
            </div>
          </form>
        )}

        {step === 3 && (
          <div className="success-message">
            <div className="success-icon">✓</div>
            <h3>Password reset successfully!</h3>
            <p>Your password has been updated. You can now sign in with your new password.</p>
            <p className="redirect-message">
              Redirecting to login in {redirectCountdown}s...
            </p>
            <Link to="/login" className="btn btn-primary btn-block">
              Go to Login Now
            </Link>
          </div>
        )}

        {step === 4 && (
          <div className="success-message">
            <div className="success-icon">✓</div>
            <h3>Check your email</h3>
            <p>
              If an account exists for <strong>{values.email}</strong>, you will receive a
              verification code shortly. The email may come from{' '}
              <strong>no-reply@verificationemail.com</strong>. Please check your
              spam/junk folder.
            </p>
            <Link to="/login" className="btn btn-primary btn-block">
              Back to Sign In
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
